import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '../Box';
import { ButtonGroup } from '../ButtonGroup';
import { Text } from '../Text';
import { Flex } from '../Flex';
import { useElementSize, useMatchMedia } from '../../utils';
import { HeaderCard } from './HeaderCard';
import { HeaderButtonObfuscated } from './HeaderButtonObfuscated';
import { ButtonGroupContext, } from '../ButtonGroup/ButtonGroupContext';
import './Header.css';
const buttonGroupContextValue = {
    size: 'small',
};
export function Header(props) {
    const { title, titleElementType, actions, inverseActions, description, filters, cards, className, variant = 'page-title', backAction, navigation, topComplement, bottomComplement, ...rest } = props;
    const titleTextVariant = {
        'page-title': 'heading-title-1',
        'page-subtitle': 'heading-title-3',
    }[variant];
    const fromExtraSmallViewport = useMatchMedia('from-extra-small');
    return (React.createElement(Box.Restricted, { ...rest, elementType: "header", className: clsx('mollie-ui-header', className) },
        React.createElement(Box, { paddingBlockEnd: "space-100", hidden: !backAction }, backAction),
        React.createElement(Flex, { direction: { base: 'row', extraSmall: 'column' }, gap: "space-200", alignItems: { base: 'center', extraSmall: 'flex-start' }, justifyContent: { base: 'space-between', extraSmall: 'flex-start' }, className: "mollie-ui-header-main" },
            React.createElement(Flex, { direction: "column", gap: "space-25", minWidth: 0 },
                React.createElement(Text.Span, { variant: "subtitle-regular", color: "secondary", textOverflow: true, hidden: !topComplement, UNSAFE_hideWhenEmpty: true }, topComplement),
                React.createElement(Text, { elementType: titleElementType !== null && titleElementType !== void 0 ? titleElementType : 'h1', variant: titleTextVariant, hidden: !title }, title),
                React.createElement(Text.Span, { variant: "subtitle-regular", color: "secondary", textOverflow: true, hidden: !bottomComplement, UNSAFE_hideWhenEmpty: true }, bottomComplement)),
            React.createElement(ButtonGroup, { className: "mollie-ui-header-actions", inverse: inverseActions, variant: "horizontal", size: "medium", hidden: !actions }, actions)),
        React.createElement(Flex, { direction: "column", gap: "space-200", className: "mollie-ui-header-extra", hidden: !Boolean(navigation || cards || description || filters) },
            navigation && (React.createElement(Header.Scrollable, { className: "mollie-ui-header-navigation" }, navigation)),
            cards && (React.createElement(Header.Scrollable, { className: "mollie-ui-header-cards" }, cards)),
            React.createElement(Text.P, { variant: "body-regular", color: "secondary", hidden: !description, className: "mollie-ui-header-description" }, description),
            filters &&
                (fromExtraSmallViewport ? (React.createElement(Flex, { wrap: true },
                    React.createElement(ButtonGroupContext.Provider, { value: buttonGroupContextValue }, filters))) : (React.createElement(Header.Scrollable, { className: "mollie-ui-header-filters" },
                    React.createElement(ButtonGroupContext.Provider, { value: buttonGroupContextValue }, filters)))))));
}
Header.displayName = 'Header';
function HeaderScrollable({ children, className, ...rest }) {
    const filtersRef = useRef(null);
    const [showScrollHint, setShowScrollHint] = useState(false);
    const preventFocus = useRef(false);
    useElementSize(filtersRef, (_size, element) => {
        setShowScrollHint(Math.ceil(element.scrollLeft + element.clientWidth) <
            element.scrollWidth - 1);
    });
    const mousePosition = useMousePosition();
    const raf = useRef(null);
    const startRaf = (element) => {
        raf.current = window.requestAnimationFrame(() => {
            const { x } = element.getBoundingClientRect();
            const xw = x + element.clientWidth;
            if (mousePosition.current.x >= x && mousePosition.current.x <= x + 33) {
                element.scrollBy({ left: -2 });
            }
            if (mousePosition.current.x <= xw && mousePosition.current.x >= xw - 33) {
                element.scrollBy({ left: 2 });
            }
            startRaf(element);
        });
    };
    return (React.createElement(Box.Restricted, { ...rest, className: clsx('mollie-ui-header-scrollable', {
            'mollie-ui-header-scrollable--with-scroll-hint': showScrollHint,
        }, className) },
        React.createElement(Box, { className: "mollie-ui-header-scrollable-content", ref: filtersRef, onScroll: (e) => {
                setShowScrollHint(Math.ceil(e.currentTarget.scrollLeft + e.currentTarget.clientWidth) <
                    e.currentTarget.scrollWidth - 1);
            }, onFocus: (e) => {
                if (!preventFocus.current) {
                    e.target.scrollIntoView({
                        block: 'nearest',
                        inline: 'center',
                    });
                }
                preventFocus.current = false;
            }, onMouseEnter: (e) => {
                startRaf(e.currentTarget);
            }, onMouseLeave: () => {
                if (raf.current) {
                    window.cancelAnimationFrame(raf.current);
                }
            }, onMouseDown: () => {
                preventFocus.current = true;
            }, onMouseUp: () => {
                preventFocus.current = false;
            }, onBlur: () => {
                preventFocus.current = false;
                if (raf.current) {
                    window.cancelAnimationFrame(raf.current);
                }
            } }, children)));
}
HeaderScrollable.displayName = 'Header.Scrollable';
Header.Scrollable = HeaderScrollable;
function useMousePosition() {
    const mousePosition = useRef({ x: 0, y: 0 });
    useEffect(() => {
        const updateMousePosition = (ev) => {
            mousePosition.current = { x: ev.clientX, y: ev.clientY };
        };
        window.addEventListener('mousemove', updateMousePosition);
        return () => {
            window.removeEventListener('mousemove', updateMousePosition);
        };
    }, []);
    return mousePosition;
}
Header.Card = HeaderCard;
Header.ButtonObfuscated = HeaderButtonObfuscated;
//# sourceMappingURL=Header.js.map